import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";
import { siteConfig } from "@/lib/site-config";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services", dropdown: "services" },
  { label: "Locations", href: "/locations", dropdown: "locations" },
  { label: "Gallery", href: "/gallery" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
];

const Navbar = () => {
  const { openQuoteForm } = useQuoteForm();
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Escape closes whichever layer is showing, so keyboard users are never
  // stuck inside the drawer or an open dropdown.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      setActiveDropdown(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const openDropdown = (id: string) => {
    clearTimeout(hoverTimeout.current);
    setActiveDropdown(id);
  };

  const closeDropdown = () => {
    hoverTimeout.current = setTimeout(() => setActiveDropdown(null), 200);
  };

  const mobileMenu = (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-[998] md:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <div
        id="mobile-menu"
        className={`fixed top-0 left-0 h-full w-80 max-w-[85%] bg-card z-[999] md:hidden transform transition-transform duration-300 ease-in-out shadow-2xl ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        aria-hidden={!open}
        /* The drawer stays mounted and slides off-screen; without inert its
           links keep their place in the tab order while it is closed. */
        {...(open ? {} : { inert: "" })}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <Link to="/" className="flex items-center" onClick={() => setOpen(false)}>
            <img
              src={siteConfig.logo}
              alt={`${siteConfig.name} logo`}
              className="h-11 w-auto"
              width={147}
              height={128}
              decoding="async"
            />
          </Link>
          <button type="button" onClick={() => setOpen(false)} className="-m-1 p-2 text-foreground/70 hover:text-foreground" aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <ul className="flex flex-col px-4 py-4 gap-1 overflow-y-auto scrollbar-hide h-[calc(100%-72px)]">
          {navLinks.map((link) =>
            link.dropdown === "services" ? (
              <li key={link.href}>
                <div className="flex items-center justify-between py-3">
                  <Link to={link.href} onClick={() => setOpen(false)} className="font-heading text-lg tracking-wide text-foreground/80 hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                  <button
                    type="button"
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="-m-1 p-2 text-foreground/80"
                    aria-label="Toggle services list"
                    aria-expanded={mobileServicesOpen}
                    aria-controls="mobile-services-list"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {mobileServicesOpen && (
                  <ul id="mobile-services-list" className="pl-4 pb-2 space-y-1">
                    {services.map((s) => (
                      <li key={s.slug}>
                        <Link to={`/services/${s.slug}`} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-foreground/70 hover:text-primary transition-colors">
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : link.dropdown === "locations" ? (
              <li key={link.href}>
                <div className="flex items-center justify-between py-3">
                  <Link to={link.href} onClick={() => setOpen(false)} className="font-heading text-lg tracking-wide text-foreground/80 hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                  <button
                    type="button"
                    onClick={() => setMobileLocationsOpen(!mobileLocationsOpen)}
                    className="-m-1 p-2 text-foreground/80"
                    aria-label="Toggle locations list"
                    aria-expanded={mobileLocationsOpen}
                    aria-controls="mobile-locations-list"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileLocationsOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {mobileLocationsOpen && (
                  <ul id="mobile-locations-list" className="pl-4 pb-2 grid grid-cols-2 gap-x-2 gap-y-1">
                    {locations.map((loc) => (
                      <li key={loc.slug}>
                        <Link to={`/locations/${loc.slug}`} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-foreground/70 hover:text-primary transition-colors">
                          {loc.name}, {loc.state}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : (
              <li key={link.href}>
                <Link to={link.href} onClick={() => setOpen(false)} className="font-heading text-lg tracking-wide text-foreground/80 hover:text-primary transition-colors block py-3">
                  {link.label}
                </Link>
              </li>
            )
          )}
          <li className="pt-4 space-y-2">
            <Button variant="default" size="sm" className="w-full" onClick={() => { openQuoteForm(); setOpen(false); }}>
              Get Free Estimate
            </Button>
            <Button asChild variant="outline" size="sm" className="w-full gap-2">
              <a href={siteConfig.phoneHref}>
                <Phone className="h-4 w-4" /> {siteConfig.phone}
              </a>
            </Button>
          </li>
        </ul>
      </div>
    </>
  );

  return (
    <>
      <nav
        aria-label="Main"
        className="sticky top-0 z-50 bg-card/85 backdrop-blur-md shadow-sm border-b border-border supports-[backdrop-filter]:bg-card/70"
      >
        <div className="container-max flex items-center justify-between px-4 py-2">
          <Link to="/" className="flex items-center">
            <img
              src={siteConfig.logo}
              alt={`${siteConfig.name} logo`}
              className="h-11 md:h-14 w-auto"
              width={147}
              height={128}
              fetchPriority="high"
              decoding="async"
            />
          </Link>
          <ul className="hidden md:flex items-center gap-7 lg:gap-8">
            {navLinks.map((link) =>
              link.dropdown ? (
                <li
                  key={link.href}
                  className="relative"
                  onMouseEnter={() => openDropdown(link.dropdown!)}
                  onMouseLeave={closeDropdown}
                  /* Focus mirrors hover so the submenu is reachable by keyboard. */
                  onFocus={() => openDropdown(link.dropdown!)}
                  onBlur={closeDropdown}
                >
                  <Link
                    to={link.href}
                    aria-haspopup="true"
                    aria-expanded={activeDropdown === link.dropdown}
                    className="nav-link-underline font-heading text-[15px] lg:text-base tracking-wide text-foreground/80 hover:text-primary transition-colors inline-flex items-center gap-1"
                  >
                    {link.label}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${activeDropdown === link.dropdown ? "rotate-180" : ""}`} />
                  </Link>
                  {activeDropdown === link.dropdown && link.dropdown === "services" && (
                    <div className="absolute top-full left-0 pt-2 z-[60]">
                      <div className="w-72 bg-popover border border-border rounded-xl shadow-xl py-2" role="menu" aria-label="Services">
                        {services.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            className="block px-4 py-2.5 text-sm text-popover-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <span className="font-semibold">{s.title}</span>
                            <span className="block text-xs text-muted-foreground">{s.subtitle}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  {activeDropdown === link.dropdown && link.dropdown === "locations" && (
                    <div className="absolute top-full left-0 pt-2 z-[60]">
                      <div className="bg-popover border border-border rounded-xl shadow-xl p-2 w-[28rem] grid grid-cols-2 gap-1" role="menu" aria-label="Locations">
                        {locations.map((loc) => (
                          <Link
                            key={loc.slug}
                            to={`/locations/${loc.slug}`}
                            className="block px-3 py-2 text-sm text-popover-foreground hover:bg-primary/10 hover:text-primary rounded-md transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <span className="font-semibold">{loc.name}, {loc.state}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              ) : (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="nav-link-underline font-heading text-[15px] lg:text-base tracking-wide text-foreground/80 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              )
            )}
            <li className="hidden lg:block">
              <Button variant="default" size="sm" onClick={openQuoteForm} className="rounded-xl font-semibold">
                Get Free Estimate
              </Button>
            </li>
          </ul>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="md:hidden text-foreground p-2"
            aria-label="Open menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </nav>
      {createPortal(mobileMenu, document.body)}
    </>
  );
};

export default Navbar;
